import subscriptionConfig from "../../shared/subscriptionConfig.json";

export const FREE_PLAN = subscriptionConfig.plans.free;
export const PRO_PLAN = subscriptionConfig.plans.pro;
export const SIGNED_OUT_VISIBLE_PROMPT_COUNT =
  subscriptionConfig.signedOutVisiblePromptCount;
export const DEFAULT_FREE_FEEDBACK_LIMIT = subscriptionConfig.freeFeedbackLimit;
export const DEFAULT_PRO_FEEDBACK_LIMIT = subscriptionConfig.proFeedbackLimit;
export const DEFAULT_FEEDBACK_LIMIT_PERIOD =
  subscriptionConfig.feedbackLimitPeriod;

function toNonNegativeNumber(value, fallback) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 0) {
    return fallback;
  }
  return parsed;
}

function toNullableLimit(value, fallback) {
  if (value === null) {
    return null;
  }

  if (value === undefined) {
    return fallback;
  }

  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 0) {
    return fallback;
  }

  return parsed;
}

export function normalizeSubscriptionProfile(profile = null) {
  const source = profile || {};
  const plan = source.plan === PRO_PLAN ? PRO_PLAN : FREE_PLAN;
  const feedbackCount = toNonNegativeNumber(source.feedbackCount, 0);
  const sourceFreeFeedbackLimit = toNonNegativeNumber(
    source.freeFeedbackLimit,
    DEFAULT_FREE_FEEDBACK_LIMIT
  );
  // Roll legacy accounts forward when the default free limit increases.
  const freeFeedbackLimit = Math.max(
    sourceFreeFeedbackLimit,
    DEFAULT_FREE_FEEDBACK_LIMIT
  );
  const proFeedbackLimit = toNullableLimit(
    source.proFeedbackLimit,
    DEFAULT_PRO_FEEDBACK_LIMIT
  );
  const appliedFeedbackLimit = plan === PRO_PLAN ? proFeedbackLimit : freeFeedbackLimit;
  const hasUnlimitedFeedback = plan === PRO_PLAN && appliedFeedbackLimit === null;
  const remainingPlanFeedback = hasUnlimitedFeedback
    ? null
    : Math.max(appliedFeedbackLimit - feedbackCount, 0);

  console.log("[normalizeSubscriptionProfile] plan:", plan, "| feedbackCount:", feedbackCount, "| freeFeedbackLimit:", freeFeedbackLimit, "| remainingFreeFeedback:", Math.max(freeFeedbackLimit - feedbackCount, 0), "| source.freeFeedbackLimit:", source.freeFeedbackLimit, "| source.feedbackCount:", source.feedbackCount);

  return {
    uid: source.uid || null,
    email: source.email || "",
    displayName: source.displayName || "",
    plan,
    feedbackCount,
    freeFeedbackLimit,
    proFeedbackLimit,
    feedbackLimitPeriod:
      source.feedbackLimitPeriod || DEFAULT_FEEDBACK_LIMIT_PERIOD,
    billingProvider: source.billingProvider || null,
    subscriptionStatus: source.subscriptionStatus || null,
    subscriptionId: source.subscriptionId || null,
    stripeCustomerId: source.stripeCustomerId || null,
    stripePriceId: source.stripePriceId || null,
    currentPeriodEnd: source.currentPeriodEnd || null,
    lastFeedbackAt: source.lastFeedbackAt || null,
    hasUnlimitedFeedback,
    appliedFeedbackLimit,
    remainingPlanFeedback,
    remainingFreeFeedback: Math.max(freeFeedbackLimit - feedbackCount, 0),
    isProUser: plan === PRO_PLAN
  };
}

export function isProUser(profile = null) {
  return normalizeSubscriptionProfile(profile).isProUser;
}

export function remainingFreeFeedback(profile = null) {
  return normalizeSubscriptionProfile(profile).remainingFreeFeedback;
}

export function canViewAllPrompts({ isAuthenticated }) {
  return Boolean(isAuthenticated);
}

export function canRequestFeedback({ isAuthenticated, profile }) {
  if (!isAuthenticated) {
    return {
      allowed: false,
      reason: "auth-required"
    };
  }

  const normalized = normalizeSubscriptionProfile(profile);

  if (normalized.hasUnlimitedFeedback) {
    return {
      allowed: true,
      reason: null,
      ...normalized
    };
  }

  if ((normalized.remainingPlanFeedback || 0) > 0) {
    return {
      allowed: true,
      reason: null,
      ...normalized
    };
  }

  return {
    allowed: false,
    reason: normalized.isProUser ? "pro-limit-reached" : "free-limit-reached",
    ...normalized
  };
}

export function getFeedbackAccessMessage({ isAuthenticated, profile }) {
  if (!isAuthenticated) {
    return "Create a free account to save your essay and get feedback.";
  }

  const normalized = normalizeSubscriptionProfile(profile);

  if (normalized.isProUser) {
    return "Unlimited feedback included.";
  }

  if (normalized.remainingFreeFeedback > 0) {
    return `${normalized.remainingFreeFeedback} feedback attempts remaining.`;
  }

  return "No feedback attempts remaining. Upgrade to continue getting feedback.";
}